import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';
import logo from '../Components/assests/giftedcitlogo-removebg-preview.png';

/**
 * LandingPage - Marketing/Conversion focused homepage
 *
 * Premium, emotional design that converts visitors into users.
 */

// Logo Component - Reusable branded logo
function Logo({ size = 'md', showText = true, className = '', onClick }) {
  const sizes = {
    sm: { img: 'h-10', text: 'text-xl' },
    md: { img: 'h-14', text: 'text-2xl' },
    lg: { img: 'h-20', text: 'text-3xl' },
    xl: { img: 'h-28', text: 'text-5xl' },
  };

  const s = sizes[size] || sizes.md;

  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-3 cursor-pointer hover:opacity-90 transition-opacity ${className}`}
    >
      <img src={logo} alt="Giftedict" className={`${s.img} w-auto drop-shadow-md`} />
      {showText && (
        <span className={`font-extrabold ${s.text} bg-gradient-to-r from-orange-600 via-amber-500 to-yellow-500 bg-clip-text text-transparent drop-shadow-sm`}>
          GIFTEDICT
        </span>
      )}
    </button>
  );
}

export function LandingPage() {
  const navigate = useNavigate();
  const posthog = usePostHog();
  const [email, setEmail] = useState('');

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-b from-amber-50/95 via-orange-50/90 to-transparent backdrop-blur-sm">
        <div className="w-full max-w-[1600px] mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Logo size="md" onClick={() => navigate('/')} />

            {/* Nav Links - Desktop */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-slate-600 hover:text-slate-900 transition-colors">Features</a>
              <a href="#how-it-works" className="text-slate-600 hover:text-slate-900 transition-colors">How it Works</a>
              <a href="#testimonials" className="text-slate-600 hover:text-slate-900 transition-colors">Stories</a>
            </div>

            {/* CTA Buttons */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/signin')}
                className="text-slate-600 hover:text-orange-600 font-medium transition-colors"
              >
                Sign In
              </button>
              <button
                onClick={() => navigate('/signup')}
                className="px-5 py-2.5 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg font-semibold shadow-md hover:shadow-lg hover:from-orange-600 hover:to-amber-600 transition-all"
              >
                Get Started Free
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 lg:px-12 bg-gradient-to-b from-amber-50/50 via-orange-50/30 to-white">
        <div className="w-full max-w-[1600px] mx-auto">
          <div className="text-center max-w-4xl mx-auto">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-amber-100 to-orange-100 text-orange-700 rounded-full text-sm font-semibold mb-6 shadow-sm">
              <span className="animate-pulse">✨</span>
              Your Personal Gift Concierge
            </div>

            {/* Headline */}
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold text-slate-900 leading-tight mb-6">
              Tell us once.{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-500 via-orange-500 to-yellow-500">
                We'll never forget.
              </span>
            </h1>

            {/* Subheadline */}
            <p className="text-xl lg:text-2xl text-slate-600 mb-8 max-w-4xl mx-auto">
              Share your special dates and recipient details once. We remember, remind, and send gifts on your behalf—so you never miss a moment.
            </p>

            {/* CTA */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={() => { posthog.capture('landing_cta_clicked', { button: 'start_for_free' }); navigate('/signup'); }}
                className="w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-xl font-semibold text-lg shadow-xl shadow-orange-500/30 hover:shadow-2xl hover:shadow-orange-500/40 hover:scale-105 transition-all"
              >
                Start for Free
              </button>
              <a
                href="#how-it-works"
                className="w-full sm:w-auto px-8 py-4 bg-white text-slate-700 rounded-xl font-semibold text-lg border-2 border-slate-200 hover:border-orange-300 hover:bg-orange-50 transition-all flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                </svg>
                See How It Works
              </a>
            </div>

            {/* Social Proof */}
            <div className="mt-12 flex items-center justify-center gap-8">
              <div className="flex -space-x-3">
                {['M', 'S', 'J', 'A', 'K'].map((initial, i) => (
                  <div
                    key={i}
                    className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold text-sm border-2 border-white ${
                      ['bg-coral-500', 'bg-gold-500', 'bg-sage-500', 'bg-blue-500', 'bg-purple-500'][i]
                    }`}
                  >
                    {initial}
                  </div>
                ))}
              </div>
              <div className="text-left">
                <div className="flex items-center gap-1 text-gold-500">
                  {[1,2,3,4,5].map(i => (
                    <svg key={i} className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <p className="text-sm text-slate-500">Loved by 10,000+ thoughtful gift-givers</p>
              </div>
            </div>
          </div>

          {/* Hero Image/Mockup */}
          <div className="mt-16 relative">
            <div className="bg-gradient-to-b from-slate-100 to-slate-200 rounded-2xl p-4 sm:p-8 shadow-2xl max-w-5xl mx-auto">
              <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                {/* Mock App Header */}
                <div className="bg-slate-50 px-4 py-3 flex items-center gap-2">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-red-400"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                    <div className="w-3 h-3 rounded-full bg-green-400"></div>
                  </div>
                </div>
                {/* Mock App Content */}
                <div className="p-6">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-semibold">M</div>
                    <div>
                      <h3 className="font-semibold text-slate-900">Mom's Birthday</h3>
                      <p className="text-sm text-slate-500">Wednesday, February 4</p>
                    </div>
                    <div className="ml-auto px-3 py-1 bg-coral-600 text-white rounded-full text-sm font-medium">6 days</div>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-3 mb-4">
                    <p className="text-sm text-slate-600"><span className="text-slate-400">Last year:</span> Cashmere scarf from Nordstrom</p>
                  </div>
                  <button className="px-4 py-2 bg-gradient-to-r from-coral-500 to-coral-600 text-white rounded-lg text-sm font-medium">
                    Find the perfect gift
                  </button>
                </div>
              </div>
            </div>

            {/* Floating Elements */}
            <div className="absolute -left-4 top-1/4 bg-white rounded-xl shadow-xl p-4 hidden lg:block animate-float">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-sage-100 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-sage-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <p className="font-medium text-slate-900">Gift saved!</p>
                  <p className="text-xs text-slate-500">Silk scarf for Mom</p>
                </div>
              </div>
            </div>

            <div className="absolute -right-4 top-1/3 bg-white rounded-xl shadow-xl p-4 hidden lg:block animate-float" style={{animationDelay: '1s'}}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gold-100 rounded-full flex items-center justify-center text-xl">
                  🎂
                </div>
                <div>
                  <p className="font-medium text-slate-900">Reminder</p>
                  <p className="text-xs text-slate-500">Jake's birthday in 3 days</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-6 lg:px-12">
        <div className="w-full max-w-[1600px] mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-display text-4xl font-bold text-slate-900 mb-4">
              Everything you need to be the most thoughtful person
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Stop forgetting birthdays. Stop giving generic gifts. Start being legendary.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
            {/* Feature 1 */}
            <div className="bg-gradient-to-br from-orange-50 to-white p-8 lg:p-10 rounded-2xl border border-orange-100 hover:shadow-xl hover:-translate-y-1 transition-all">
              <div className="w-14 h-14 bg-gradient-to-br from-orange-400 to-amber-500 rounded-xl flex items-center justify-center text-2xl mb-6 shadow-lg shadow-orange-500/20">
                🎯
              </div>
              <h3 className="font-display text-xl font-bold text-slate-900 mb-3">
                Never Forget Again
              </h3>
              <p className="text-slate-600">
                Smart reminders at 2 weeks, 1 week, and 3 days before every occasion.
                We've got your back.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="bg-gradient-to-br from-amber-50 to-white p-8 lg:p-10 rounded-2xl border border-amber-100 hover:shadow-xl hover:-translate-y-1 transition-all">
              <div className="w-14 h-14 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-xl flex items-center justify-center text-2xl mb-6 shadow-lg shadow-amber-500/20">
                💡
              </div>
              <h3 className="font-display text-xl font-bold text-slate-900 mb-3">
                Gift Memory Bank
              </h3>
              <p className="text-slate-600">
                Track every gift you've given. Never accidentally repeat a present or
                forget what worked.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="bg-gradient-to-br from-yellow-50 to-white p-8 lg:p-10 rounded-2xl border border-yellow-100 hover:shadow-xl hover:-translate-y-1 transition-all">
              <div className="w-14 h-14 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-xl flex items-center justify-center text-2xl mb-6 shadow-lg shadow-yellow-500/20">
                ✨
              </div>
              <h3 className="font-display text-xl font-bold text-slate-900 mb-3">
                Interest Tracking
              </h3>
              <p className="text-slate-600">
                Remember their hobbies, sizes, favorites. When gift time comes,
                you'll know exactly what they love.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works - Premium Animated Section */}
      <section id="how-it-works" className="py-24 px-6 lg:px-12 bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          {/* Floating orbs */}
          <div className="absolute top-20 left-10 w-72 h-72 bg-orange-500/10 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}} />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-yellow-500/5 rounded-full blur-3xl" />

          {/* Grid pattern */}
          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:60px_60px]" />

          {/* Animated particles */}
          <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-orange-400 rounded-full animate-ping" style={{animationDuration: '2s'}} />
          <div className="absolute top-3/4 right-1/4 w-2 h-2 bg-amber-400 rounded-full animate-ping" style={{animationDuration: '3s', animationDelay: '0.5s'}} />
          <div className="absolute top-1/2 right-1/3 w-2 h-2 bg-yellow-400 rounded-full animate-ping" style={{animationDuration: '2.5s', animationDelay: '1s'}} />
        </div>

        <div className="w-full max-w-[1400px] mx-auto relative z-10">
          {/* Section Header */}
          <div className="text-center mb-20">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 backdrop-blur-sm border border-white/10 text-orange-300 rounded-full text-sm font-medium mb-6">
              <span className="w-2 h-2 bg-orange-400 rounded-full animate-pulse" />
              Simple 4-Step Journey
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              How the
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-amber-400 to-yellow-400"> magic </span>
              happens
            </h2>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto">
              From forgotten birthdays to legendary gift-giver in four simple steps
            </p>
          </div>

          {/* Timeline Steps */}
          <div className="relative">
            {/* Connecting Line with Graphics - Desktop */}
            <div className="hidden lg:block absolute top-32 left-[12%] right-[12%] h-0.5 bg-gradient-to-r from-orange-500/30 via-amber-500/50 to-yellow-500/30" />

            {/* Animated progress line */}
            <div className="hidden lg:block absolute top-32 left-[12%] h-0.5 bg-gradient-to-r from-orange-500 via-amber-500 to-yellow-500 animate-pulse" style={{width: '25%', animationDuration: '3s'}} />

            {/* Arrow decorations */}
            <div className="hidden lg:flex absolute top-[7.75rem] left-[33%] items-center justify-center">
              <svg className="w-4 h-4 text-amber-400 animate-pulse" style={{animationDelay: '0.5s'}} fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="hidden lg:flex absolute top-[7.75rem] left-[58%] items-center justify-center">
              <svg className="w-4 h-4 text-amber-400 animate-pulse" style={{animationDelay: '1s'}} fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="hidden lg:flex absolute top-[7.75rem] left-[83%] items-center justify-center">
              <svg className="w-4 h-4 text-yellow-400 animate-pulse" style={{animationDelay: '1.5s'}} fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6">
              {/* Step 1 - Add Your Circle */}
              <div className="group relative">
                <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8 h-full hover:border-orange-500/50 hover:bg-white/10 transition-all duration-500 hover:-translate-y-2">
                  {/* Step Number */}
                  <div className="relative mb-8">
                    <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-amber-500 rounded-2xl flex items-center justify-center text-2xl font-bold text-white shadow-lg shadow-orange-500/30 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                      1
                    </div>
                    {/* Glow effect */}
                    <div className="absolute inset-0 w-16 h-16 bg-orange-500/30 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>

                  {/* Icon Illustration */}
                  <div className="relative h-32 mb-6 flex items-center justify-center">
                    <div className="relative">
                      {/* Central avatar */}
                      <div className="w-20 h-20 bg-gradient-to-br from-orange-400 to-amber-500 rounded-full flex items-center justify-center text-3xl shadow-xl group-hover:scale-110 transition-transform">
                        💝
                      </div>
                      {/* Orbiting avatars */}
                      <div className="absolute -top-2 -right-4 w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center text-white text-sm font-bold shadow-lg animate-bounce" style={{animationDuration: '2s'}}>
                        M
                      </div>
                      <div className="absolute -bottom-2 -left-4 w-10 h-10 bg-gradient-to-br from-pink-400 to-rose-500 rounded-full flex items-center justify-center text-white text-sm font-bold shadow-lg animate-bounce" style={{animationDuration: '2.2s', animationDelay: '0.3s'}}>
                        S
                      </div>
                      <div className="absolute top-1/2 -right-6 w-8 h-8 bg-gradient-to-br from-purple-400 to-purple-600 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-lg animate-bounce" style={{animationDuration: '2.4s', animationDelay: '0.6s'}}>
                        J
                      </div>
                    </div>
                  </div>

                  <h3 className="text-xl font-bold text-white mb-3">Add Your Circle</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">
                    Add everyone who matters — family, friends, colleagues. Tell us about their birthdays, anniversaries, and special moments.
                  </p>
                </div>
              </div>

              {/* Step 2 - We Remember Everything */}
              <div className="group relative">
                <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8 h-full hover:border-amber-500/50 hover:bg-white/10 transition-all duration-500 hover:-translate-y-2">
                  {/* Step Number */}
                  <div className="relative mb-8">
                    <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-yellow-500 rounded-2xl flex items-center justify-center text-2xl font-bold text-white shadow-lg shadow-amber-500/30 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                      2
                    </div>
                    <div className="absolute inset-0 w-16 h-16 bg-amber-500/30 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>

                  {/* Icon Illustration */}
                  <div className="relative h-32 mb-6 flex items-center justify-center">
                    <div className="relative">
                      {/* Calendar/Brain icon */}
                      <div className="w-20 h-20 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-2xl flex items-center justify-center text-3xl shadow-xl group-hover:scale-110 transition-transform">
                        🧠
                      </div>
                      {/* Floating data points */}
                      <div className="absolute -top-4 right-0 px-2 py-1 bg-white/20 backdrop-blur rounded-lg text-[10px] text-white font-medium animate-pulse">
                        Size: M
                      </div>
                      <div className="absolute -bottom-4 -left-2 px-2 py-1 bg-white/20 backdrop-blur rounded-lg text-[10px] text-white font-medium animate-pulse" style={{animationDelay: '0.5s'}}>
                        ☕ Coffee lover
                      </div>
                      <div className="absolute top-0 -left-6 px-2 py-1 bg-white/20 backdrop-blur rounded-lg text-[10px] text-white font-medium animate-pulse" style={{animationDelay: '1s'}}>
                        📚 Books
                      </div>
                    </div>
                  </div>

                  <h3 className="text-xl font-bold text-white mb-3">We Remember Everything</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">
                    Track interests, sizes, favorites, and wishlists. Every detail is securely stored and ready when you need it.
                  </p>
                </div>
              </div>

              {/* Step 3 - Perfect Gift Discovery */}
              <div className="group relative">
                <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8 h-full hover:border-yellow-500/50 hover:bg-white/10 transition-all duration-500 hover:-translate-y-2">
                  {/* Step Number */}
                  <div className="relative mb-8">
                    <div className="w-16 h-16 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-2xl flex items-center justify-center text-2xl font-bold text-white shadow-lg shadow-yellow-500/30 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                      3
                    </div>
                    <div className="absolute inset-0 w-16 h-16 bg-yellow-500/30 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>

                  {/* Icon Illustration */}
                  <div className="relative h-32 mb-6 flex items-center justify-center">
                    <div className="relative">
                      {/* Gift box with sparkles */}
                      <div className="w-20 h-20 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-2xl flex items-center justify-center text-3xl shadow-xl group-hover:scale-110 transition-transform">
                        🎁
                      </div>
                      {/* Sparkles */}
                      <div className="absolute -top-2 -right-2 text-2xl animate-spin" style={{animationDuration: '3s'}}>✨</div>
                      <div className="absolute -bottom-1 -left-3 text-xl animate-spin" style={{animationDuration: '4s', animationDirection: 'reverse'}}>⭐</div>
                      <div className="absolute top-1/2 -right-5 text-lg animate-ping" style={{animationDuration: '2s'}}>💫</div>
                    </div>
                  </div>

                  <h3 className="text-xl font-bold text-white mb-3">Find Perfect Gifts</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">
                    We help you discover thoughtful gift ideas based on their interests. No more last-minute panic buying.
                  </p>
                </div>
              </div>

              {/* Step 4 - You're the Hero */}
              <div className="group relative">
                <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8 h-full hover:border-orange-500/50 hover:bg-white/10 transition-all duration-500 hover:-translate-y-2">
                  {/* Step Number */}
                  <div className="relative mb-8">
                    <div className="w-16 h-16 bg-gradient-to-br from-orange-500 via-amber-500 to-yellow-500 rounded-2xl flex items-center justify-center text-2xl font-bold text-white shadow-lg shadow-orange-500/30 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                      4
                    </div>
                    <div className="absolute inset-0 w-16 h-16 bg-orange-500/30 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>

                  {/* Icon Illustration */}
                  <div className="relative h-32 mb-6 flex items-center justify-center">
                    <div className="relative">
                      {/* Trophy/celebration */}
                      <div className="w-20 h-20 bg-gradient-to-br from-orange-400 via-amber-400 to-yellow-400 rounded-full flex items-center justify-center text-3xl shadow-xl group-hover:scale-110 transition-transform">
                        🏆
                      </div>
                      {/* Confetti */}
                      <div className="absolute -top-4 left-0 text-xl animate-bounce" style={{animationDuration: '1s'}}>🎉</div>
                      <div className="absolute -top-2 right-0 text-lg animate-bounce" style={{animationDuration: '1.2s', animationDelay: '0.2s'}}>🎊</div>
                      <div className="absolute -bottom-2 -right-4 text-xl animate-bounce" style={{animationDuration: '1.4s', animationDelay: '0.4s'}}>💖</div>
                      <div className="absolute -bottom-4 left-2 text-lg animate-bounce" style={{animationDuration: '1.6s', animationDelay: '0.6s'}}>😍</div>
                    </div>
                  </div>

                  <h3 className="text-xl font-bold text-white mb-3">Be the Thoughtful One</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">
                    They'll think you remembered everything on your own. You get the credit. We keep your secret safe.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom CTA */}
          <div className="text-center mt-16">
            <button
              onClick={() => navigate('/signup')}
              className="group inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-orange-500 via-amber-500 to-yellow-500 text-white rounded-2xl font-semibold text-lg shadow-xl shadow-orange-500/30 hover:shadow-2xl hover:shadow-orange-500/40 hover:scale-105 transition-all"
            >
              Start Your Journey
              <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </button>
            <p className="text-slate-500 text-sm mt-4">
              Free forever for up to 10 people
            </p>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-20 px-6 lg:px-12">
        <div className="w-full max-w-[1600px] mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-display text-4xl font-bold text-slate-900 mb-4">
              Join the thoughtful revolution
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
            {/* Testimonial 1 */}
            <div className="bg-white p-8 lg:p-10 rounded-2xl shadow-lg border border-slate-100">
              <div className="flex items-center gap-1 text-gold-500 mb-4">
                {[1,2,3,4,5].map(i => (
                  <svg key={i} className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
              <p className="text-slate-600 mb-6">
                "My wife cried when I remembered her favorite author's new book release.
                Giftedict made me look like a genius."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-semibold">M</div>
                <div>
                  <p className="font-semibold text-slate-900">Michael R.</p>
                  <p className="text-sm text-slate-500">Recovering gift-forgetter</p>
                </div>
              </div>
            </div>

            {/* Testimonial 2 */}
            <div className="bg-white p-8 lg:p-10 rounded-2xl shadow-lg border border-slate-100">
              <div className="flex items-center gap-1 text-gold-500 mb-4">
                {[1,2,3,4,5].map(i => (
                  <svg key={i} className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
              <p className="text-slate-600 mb-6">
                "I manage gifts for 40+ family members. Before Giftedict, I was drowning.
                Now I'm the family hero."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-coral-400 to-coral-600 flex items-center justify-center text-white font-semibold">S</div>
                <div>
                  <p className="font-semibold text-slate-900">Sarah K.</p>
                  <p className="text-sm text-slate-500">Family gift coordinator</p>
                </div>
              </div>
            </div>

            {/* Testimonial 3 */}
            <div className="bg-white p-8 lg:p-10 rounded-2xl shadow-lg border border-slate-100">
              <div className="flex items-center gap-1 text-gold-500 mb-4">
                {[1,2,3,4,5].map(i => (
                  <svg key={i} className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
              <p className="text-slate-600 mb-6">
                "Finally remembered my mom's dress size without awkwardly asking.
                The look on her face was priceless."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center text-white font-semibold">J</div>
                <div>
                  <p className="font-semibold text-slate-900">James T.</p>
                  <p className="text-sm text-slate-500">Detail rememberer</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 lg:px-12 bg-gradient-to-br from-orange-500 via-amber-500 to-yellow-500">
        <div className="w-full max-w-[1200px] mx-auto text-center">
          <h2 className="font-display text-4xl sm:text-5xl font-bold text-white mb-6 drop-shadow-sm">
            Ready to become legendary?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Join thousands of thoughtful gift-givers who never miss an occasion.
            Free forever for up to 10 people.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 max-w-xl mx-auto">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              className="w-full px-5 py-4 rounded-xl text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-4 focus:ring-white/30 shadow-lg"
            />
            <button
              onClick={() => navigate('/signup')}
              className="w-full sm:w-auto px-8 py-4 bg-slate-900 text-white rounded-xl font-semibold text-lg hover:bg-slate-800 transition-all whitespace-nowrap shadow-lg"
            >
              Get Started
            </button>
          </div>

          <p className="text-white/80 text-sm mt-4">
            No credit card required. Set up in 60 seconds.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 lg:px-12 bg-slate-900">
        <div className="w-full max-w-[1600px] mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <Logo size="md" onClick={() => navigate('/')} />

            <div className="flex items-center gap-6 text-slate-400">
              <a href="#" className="hover:text-white transition-colors">Privacy</a>
              <a href="#" className="hover:text-white transition-colors">Terms</a>
              <a href="/contact" className="hover:text-white transition-colors">Contact</a>
            </div>

            <p className="text-slate-500 text-sm">
              Made with 💝 for thoughtful people
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;
